import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaChevronLeft, FaQrcode } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { getEventosPaginados, createEvento, updateEvento, toggleEventoStatus, deleteEvento } from '../../../components/http/Actions/EventosAction';

// Components
import EventoCards from './components/EventoCards';
import CreateEventoModal from './components/CreateEventoModal';
import EditEventoModal from './components/EditEventoModal';
import SeccionEventoModal from './components/SeccionEventoModal';
import TicketEventoModal from './components/TicketEventoModal';
import TicketScannerModal from './components/TicketScannerModal';

import Notification from '../../../components/Notification';
import Pagination from '../../../components/Pagination';
import SearchBar from '../../../components/SearchBar';

const EventoConfig = () => {
  // Estado para almacenar la lista de eventos
  const [eventos, setEventos] = useState([]);

  // Secciones y tickets
  const [showSeccionesModal, setShowSeccionesModal] = useState(false);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [showScannerModal, setShowScannerModal] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState(null);
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  
  // Estados para modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Estado para nuevo evento
  const [newEvento, setNewEvento] = useState({
    titulo: '',
    descripcion: '',
    fecha: '',
    imagen: '',
    mapaLugar: '',
    duracionMinutos: 0,
    lugarId: '',
    comediantesIds: [],
    tagsIds: []
  });

  // Estado para editar evento
  const [editEvento, setEditEvento] = useState(null);
  
  // token
  const token = localStorage.getItem("token");
  // Estado para errores y notificaciones
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar eventos con paginación
  const fetchEventos = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    setError('');
    
    try {
      await getEventosPaginados(
        (data) => {
          if (data && data.data) {
            setEventos(data.data.data);
            setPagination({
              currentPage: data.data.currentPage,
              pageSize: data.data.pageSize,
              totalCount: data.data.totalCount,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setEventos([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        token
      );
    } catch (error) {
      console.error("Error fetching eventos:", error);
      showNotification("Error al cargar los eventos", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setError('');
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Cargar eventos cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchEventos(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchEventos, pagination.pageSize]);

  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewEvento({
      titulo: '',
      descripcion: '',
      fecha: '',
      imagen: '',
      mapaLugar: '',
      duracionMinutos: 0,
      lugarId: '',
      comediantesIds: [],
      tagsIds: []
    });
    setShowCreateModal(true);
  };

  // Función para crear evento
  const handleCreateSubmit = async (e, newEventoData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await createEvento(newEventoData, setError, token);
      fetchEventos(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Evento "${newEventoData.titulo}" creado con éxito`, "success");
      setShowCreateModal(false);
    } catch (error) {
      console.error("Error creating evento:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para manejar la edición de eventos
  const handleEditEvento = (evento) => {
    setEditEvento({
      titulo: evento.titulo,
      descripcion: evento.descripcion,
      fecha: evento.fecha.slice(0, 16), // Formato datetime-local
      imagen: evento.imagen || '',
      mapaLugar: evento.mapaLugar || '',
      duracionMinutos: evento.duracionMinutos,
      lugarId: evento.lugar?.lugarId || '',
      habilitado: evento.habilitado,
      comediantesIds: evento.comediantes?.map(c => c.comedianteId) || [],
      tagsIds: evento.tags?.map(t => t.tagId) || []
    });
    setSelectedEvento(evento);
    setShowEditModal(true);
  };

  // Función para actualizar evento
  const handleUpdateSubmit = async (e, eventoData) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await updateEvento(selectedEvento.eventoId, eventoData, setError, token);
      fetchEventos(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification(`Evento "${eventoData.titulo}" actualizado con éxito`, "success");
      setShowEditModal(false);
      setSelectedEvento(null);
      setEditEvento(null);
    } catch (error) {
      console.error("Error updating evento:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para cambiar estado del evento
  const handleToggleStatus = async (eventoId) => {
    try {
      await toggleEventoStatus(eventoId, setError, token);
      fetchEventos(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification("Estado del evento actualizado", "success");
    } catch (error) {
      showNotification("Error al cambiar el estado", "error");
    }
  };

  // Función para eliminar evento
  const handleDeleteEvento = async (eventoId) => {
    try {
      await deleteEvento(eventoId, setError, token);
      fetchEventos(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
      showNotification("Evento eliminado exitosamente", "success");
    } catch (error) {
      showNotification("Error al eliminar el evento", "error");
    }
  };

  // Función para manejar la gestión de secciones
  const handleManageSecciones = (evento) => {
    setSelectedEvento(evento);
    setShowSeccionesModal(true);
  };

  // Función para manejar la gestión de tickets
  const handleManageTickets = (evento) => {
    setSelectedEvento(evento);
    setShowTicketsModal(true);
  };

  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Eventos</h1>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => setShowScannerModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
            >
              <FaQrcode className="mr-2" />
              Escáner
            </button>
            <button 
              onClick={openCreateModal}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
            >
              <FaPlus className="mr-2" />
              Nuevo Evento
            </button>
          </div>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Eventos cards */}
        {!isLoading && !error && (
          <EventoCards  
            eventos={eventos}
            onManageSecciones={handleManageSecciones}
            onManageTickets={handleManageTickets}
            onEditEvento={handleEditEvento}
            onToggleStatus={handleToggleStatus}
            onDeleteEvento={handleDeleteEvento}
          />
        )}

        {/* Paginación */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Create modal */}
      {showCreateModal && (
        <CreateEventoModal 
          newEvento={newEvento}
          setNewEvento={setNewEvento}
          handleCreateSubmit={handleCreateSubmit}
          isLoading={isLoading}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit modal */}
      {showEditModal && editEvento && (
        <EditEventoModal 
          editEvento={editEvento}
          setEditEvento={setEditEvento}
          handleUpdateSubmit={handleUpdateSubmit}
          isLoading={isLoading}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvento(null);
            setEditEvento(null);
          }}
        />
      )}

      {/* Secciones modal */}
      {showSeccionesModal && selectedEvento && (
        <SeccionEventoModal 
          evento={selectedEvento}
          onClose={() => {
            setShowSeccionesModal(false);
            setSelectedEvento(null);
          }}
          showNotification={showNotification}
        />
      )}

      {/* Tickets modal */}
      {showTicketsModal && selectedEvento && (
        <TicketEventoModal 
          evento={selectedEvento}
          onClose={() => {
            setShowTicketsModal(false);
            setSelectedEvento(null);
          }}
          showNotification={showNotification}
        />
      )}

      {/* Scanner modal */}
      {showScannerModal && (
        <TicketScannerModal 
          onClose={() => setShowScannerModal(false)}
          showNotification={showNotification}
        />
      )}
    </div>
  );
};

export default EventoConfig;